import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import { EmptyArray, last, nonNullable } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { useCallback, useEffect, useMemo, useContext } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { getFlightLoyaltyProductType, getProtectedRoundTripPrice } from 'lib/flights/flightUtils'
import { trackClientEvent } from 'analytics/analytics'
import useInsuranceLabels from 'hooks/Insurance/useInsuranceLabels'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import FlightsLoyaltyContext from 'contexts/Flights/FlightsLoyaltyContext'
import getLoyaltyEarnKey from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKey'
import { useAppSelector } from 'hooks/reduxHooks'
import { getFlightSegmentsState } from 'selectors/flightsSelectors'
import isLoyaltyCalculationRequestWithBonus from 'loyalty/lib/pointsCalculation/isLoyaltyCalculationRequestWithBonus'
import { CHARTER } from 'constants/provider'
import { FlightsClassTypes, maxFlightChildAge, maxFlightInfantAge } from 'constants/flight'
import { FormattedMessage, useIntl } from 'react-intl'
import FareFamilyUpsellOptionCard, { type FareFamilyUpsellOptionPrice } from './FareFamilyUpsellOptionCard'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import useSingleLegFlightTilePriceCaption from '../SingleLegFlightTile/useSingleLegFlightTilePriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import CharterRewardGreatValueBadge from 'components/OfferPage/Filters/CharterFlight/CharterRewardGreatValueBadge'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import useCharterRewardAvailability from 'hooks/Flights/useCharterRewardAvailability'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import { REWARD_PREVIEW_RATE_BY_CABIN, REWARD_PREVIEW_RATE_ONE_WAY_BY_CABIN, REWARD_PREVIEW_RATE_OVERRIDES_BY_JOURNEY_KEY } from 'lib/maldivesCharter/classicRewardPricing'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import LoyaltyJoinModal from 'loyalty/components/LoyaltyJoinModal'

const UPSELL_INCLUSION_FEATURE_TYPES: Array<App.FareFamilyFeatureType> = [
  'carry_on_bag',
  'carry_on_baggage',
  'checked_baggage',
  'meals',
  'seat_reservation',
  'refunds',
  'changes',
  'insurance',
]

const getPriceValue = (price: number) => {
  // Check for -0, in that case we should show +0. 0 === -0 returns true
  return Math.round(price) === 0 ? 0 : price
}

const FareOptionPane = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: ${rem(72)};
  padding-block: ${rem(24)} ${rem(10)};
  padding-inline: ${rem(12)} ${rem(44)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};
  background-color: ${props => props.theme.palette.neutral.default.eight};

  &.is-selected {
    border-color: ${props => props.theme.palette.neutral.default.one};
  }

  &.is-logged-out {
    padding-inline-end: ${rem(12)};
  }

  &.is-disabled {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }
`

const FareOptionActionSlot = styled.div`
  position: absolute;
  top: 50%;
  inset-inline-end: ${rem(12)};
  transform: translateY(-50%);
`

const RewardHeadingRow = styled(Group)`
  padding-inline-end: ${rem(32)};

  .is-logged-out & {
    padding-inline-end: 0;
  }
`

const RewardPriceCaption = styled(PriceRowCaption)`
  visibility: visible;

  &.is-hidden {
    visibility: hidden;
  }
`

const RewardOnlyHeading = styled(Group)`
  padding-inline: ${rem(4)};
`

interface Props {
  selected: boolean
  fareFamily: App.FlightFareFamily
  features?: Array<App.FareFamilyFeature>
  relativePrice?: number
  relativePriceDivisor?: number
  relativePriceUnit?: 'adult' | 'traveller'
  relativePriceTravellerCount?: number
  onSelect?: (data: { fareFamily: App.FlightFareFamily, pointsCalculationRequests?: Array<App.LoyaltyPointsEarnCalculationRequest | undefined>, isClassicReward?: boolean, rewardPointsTotal?: number }) => void
  onShowFareRules?: (urls: string) => void
  isReturnType?: boolean
  journey: App.JourneyV2
  departingFareFamily?: App.FlightFareFamily
  rewardDepartingFareFamily?: App.FlightFareFamily
  returningFareFamily?: App.FlightFareFamily
  occupancies?: App.Occupants
  showClassicRewardOption?: boolean
  showCashOption?: boolean
  isClassicRewardSelected?: boolean
  isPerAdultPrice?: boolean
  /**
   * Whether this is genuinely the bundled hotel + flight flow, as distinct from
   * `isPerAdultPrice` being false (which is also true for a standalone mixed-traveller
   * search). Selects the all-travellers price caption: only the bundled flow shows
   * "Hotel and return flight total" - a standalone mixed-traveller search has no hotel
   * and must keep the generic "For all travellers" caption.
   */
  isBundledFlow?: boolean
  overrideTotalCost?: number
  overrideAdultCost?: number
  luxPlusMemberCost?: number
  luxPlusMemberAdultCost?: number
  luxPlusRegularCost?: number
  luxPlusRegularAdultCost?: number
  isDomestic: boolean
  isMultiCity?: boolean
  bundledHotelPrice?: number
  /**
   * Cheapest available fare for the not-yet-selected returning leg of a two-one-ways
   * bundle (regular, non-charter feeder flow - charter feeder uses `feederBudgetLabel`
   * instead). Added alongside `bundledHotelPrice` so the departing screen shows a real
   * package-total estimate rather than hotel + this leg alone.
   */
  cheapestReturningPrice?: number
  /**
   * FLY-5300: aggregate (all travellers) absolute hotel + flights package total
   * override for feeder bundle (Maldives charter non-MEL feeder origin). When
   * defined, replaces the standard relativePrice math on the price row regardless
   * of `isReturnType` / `isPerAdultPrice`.
   */
  feederBudgetLabel?: number
  /**
   * Non-member reference-price counterpart to `feederBudgetLabel`: the same feeder-bundle
   * baseline/budget/charter rules, run on this fare family's regular (undiscounted) cost.
   * Used for the LuxPlus "Non-member $X" comparison price so it never drifts from
   * `feederBudgetLabel` the way the plain `bundledHotelPrice + fareCost` formula did.
   */
  feederRegularBudgetLabel?: number
}

interface ClassicRewardFareOptionProps {
  fareFamily: App.FlightFareFamily
  rewardDepartingFareFamily?: App.FlightFareFamily
  returningFareFamily?: App.FlightFareFamily
  isReturnType: boolean
  occupancies: App.Occupants
  selected: boolean
  onSelect: (rewardPointsTotal: number) => void
}

function ClassicRewardFareOption({
  fareFamily,
  rewardDepartingFareFamily,
  returningFareFamily,
  isReturnType,
  occupancies,
  selected,
  onSelect,
}: ClassicRewardFareOptionProps) {
  const intl = useIntl()
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const showModal = useContext(ModalContext)
  const isLoggedOut = !memberId
  const isNonMember = !!memberId && !hasLoyaltyAccount
  const isLocked = isLoggedOut || isNonMember
  const seatEligiblePassengers = useMemo(
    () => sumUpOccupancies(
      [occupancies],
      new Set<App.OccupantType>(['adults', 'children', 'teenagers']),
      { maxInfantAge: maxFlightInfantAge, maxChildAge: maxFlightChildAge },
    ),
    [occupancies],
  )
  const rewardLegs = useMemo(() => {
    if (!fareFamily.journeyKey) return []

    const departingRewardFareFamily = rewardDepartingFareFamily ?? fareFamily
    if (isReturnType) {
      if (!returningFareFamily?.journeyKey) return []
      return [
        {
          journeyKey: departingRewardFareFamily.journeyKey ?? fareFamily.journeyKey,
          legTotal: departingRewardFareFamily.costWithInsurance?.all ?? departingRewardFareFamily.cost.all,
        },
        {
          journeyKey: returningFareFamily.journeyKey,
          legTotal: returningFareFamily.costWithInsurance?.all ?? returningFareFamily.cost.all,
        },
      ]
    }

    if (!fareFamily.journeyKey) return []
    return [{
      journeyKey: fareFamily.journeyKey,
      legTotal: fareFamily.costWithInsurance?.all ?? fareFamily.cost.all,
    }]
  }, [fareFamily, isReturnType, returningFareFamily, rewardDepartingFareFamily])
  const rewardAvailability = useCharterRewardAvailability({
    legs: rewardLegs,
    passengerCount: seatEligiblePassengers,
    disabled: isLocked || rewardLegs.length === 0,
    refetchOnMount: 'always',
  })
  const rewardOptionAvailable = rewardAvailability.available && rewardAvailability.pointsPerPerson !== undefined
  const rewardOptionDisabled = rewardAvailability.isLoading || !rewardOptionAvailable
  const rewardUnavailableLabel = intl.formatMessage({
    id: 'tF5YRUBsGBktuoMdsdJs',
    defaultMessage: 'Sold out!',
    description: 'Label shown when a Classic Rewards flight fare option is not available',
  })
  const rewardLoadingLabel = intl.formatMessage({
    id: 'W7qKcEC8WyMQA6vkGSfn',
    defaultMessage: 'Checking availability',
    description: 'Label shown while checking Classic Rewards flight fare availability',
  })
  const societeRewardsSubline = intl.formatMessage({
    id: 'oDGwSeNRaY8VwqgIVTRP',
    defaultMessage: '{loyaltyProgramName} Rewards',
    description: 'Inline label on Maldives charter reward fare cards naming the loyalty program',
  }, { loyaltyProgramName: getLoyaltyProgramDisplayName() })
  const loginToViewLabel = intl.formatMessage({
    id: 'Nns_jJsqfO8wDATc2d-P',
    defaultMessage: 'Log in to view',
    description: 'Primary CTA in the Maldives charter "Select a fare" modal when a logged-out user sees a reward fare option. Clicking opens the account access (login) modal.',
  })
  const enrollLabel = intl.formatMessage({
    id: 'u1AVi50L43eqeNElJ9DP',
    defaultMessage: 'Join {loyaltyProgramName}',
    description: 'Button label on the mobile fare carousel card when a logged-in user without Société membership views a reward fare.',
  }, { loyaltyProgramName: getLoyaltyProgramDisplayName() })
  const selectLabel = intl.formatMessage({
    id: 'aBpkOlYk7Trbga9k4IiO',
    defaultMessage: 'Select',
    description: 'Button text to select a flight in desktop tile component',
  })
  const previewRate = useMemo(() => {
    if (fareFamily.journeyKey && REWARD_PREVIEW_RATE_OVERRIDES_BY_JOURNEY_KEY[fareFamily.journeyKey]) {
      return REWARD_PREVIEW_RATE_OVERRIDES_BY_JOURNEY_KEY[fareFamily.journeyKey]
    }

    const raw = fareFamily.cabin.trim().toLowerCase()
    const cabinToFareClass: Partial<Record<string, FlightsClassTypes>> = {
      'economy': FlightsClassTypes.ECONOMY,
      'economy extra': FlightsClassTypes.LUXROW,
      'economy extra for one': FlightsClassTypes.LUXROW,
      'premium': FlightsClassTypes.PREMIUM,
      'premium economy': FlightsClassTypes.PREMIUM,
      'business': FlightsClassTypes.BUSINESS,
    }
    const fareClass = cabinToFareClass[raw]
    if (!fareClass) return undefined
    return isReturnType
      ? REWARD_PREVIEW_RATE_BY_CABIN[fareClass]
      : REWARD_PREVIEW_RATE_ONE_WAY_BY_CABIN[fareClass]
  }, [fareFamily.cabin, fareFamily.journeyKey, isReturnType])
  const handleRewardSelect = useCallback(() => {
    if (isLoggedOut) {
      showModal(<AccountAccessModal />)
      return
    }
    if (isNonMember) {
      showModal(<LoyaltyJoinModal onAfterClose={noop} primaryActionCta={enrollLabel} />)
      return
    }
    if (rewardOptionDisabled) return
    onSelect(rewardAvailability.pointsPerPerson! * seatEligiblePassengers)
  }, [isLoggedOut, isNonMember, onSelect, rewardOptionDisabled, showModal, rewardAvailability.pointsPerPerson, seatEligiblePassengers, enrollLabel])

  return (
    <FareOptionPane className={[selected && 'is-selected', isLocked && 'is-logged-out', !isLocked && rewardOptionDisabled && 'is-disabled'].filter(Boolean).join(' ')}>
      <CharterRewardGreatValueBadge />
      <VerticalSpacer gap={4}>
        <RewardHeadingRow direction="horizontal" gap={8} verticalAlign="center">
          <ClassicRewardsSealIcon size={20} colour="highlight-primary-dark" aria-hidden />
          <BodyText variant="small" weight="bold" colour="highlight-primary-dark">
            {societeRewardsSubline}
          </BodyText>
        </RewardHeadingRow>
        {isLocked && <Group direction="vertical">
          {previewRate && <Group direction="horizontal" gap={4} verticalAlign="center">
            <BodyText variant="medium" weight="bold">
              <FormatLoyaltyPoints points={previewRate.pointsPerPerson} />
            </BodyText>
            <RewardPriceCaption>
              {intl.formatMessage({
                id: 'u168rTnPZ0U0jZoffSme',
                defaultMessage: '+ {cashPrice} /adult',
                description: 'Cash co-payment shown after the points price for a Classic Rewards standalone charter fare option',
              }, { cashPrice: <FormatCurrency value={previewRate.cashPerPerson} /> })}
            </RewardPriceCaption>
          </Group>}
          <div style={{ marginBlockStart: rem(8) }}>
            <TextButton kind="secondary" fit="flex" onClick={handleRewardSelect}>
              {isNonMember ? enrollLabel : loginToViewLabel}
            </TextButton>
          </div>
        </Group>}
        {!isLocked && <Group direction="vertical">
          <BodyText variant="medium" weight="bold">
            {rewardAvailability.isLoading && rewardLoadingLabel}
            {!rewardAvailability.isLoading && !rewardOptionAvailable && rewardUnavailableLabel}
            {!rewardAvailability.isLoading && rewardOptionAvailable && rewardAvailability.pointsPerPerson !== undefined &&
              <FormatLoyaltyPoints points={rewardAvailability.pointsPerPerson} />}
          </BodyText>
          <RewardPriceCaption className={!rewardOptionAvailable ? 'is-hidden' : undefined}>
            {intl.formatMessage({
              id: 'u168rTnPZ0U0jZoffSme',
              defaultMessage: '+ {cashPrice} /adult',
              description: 'Cash co-payment shown after the points price for a Classic Rewards standalone charter fare option',
            }, {
              cashPrice: <FormatCurrency value={rewardAvailability.cashCoPayPerPerson ?? 0} />,
            })}
          </RewardPriceCaption>
        </Group>}
      </VerticalSpacer>
      {!isLocked && <FareOptionActionSlot>
        <TextButton kind="secondary" shape="round" onClick={handleRewardSelect} disabled={rewardOptionDisabled}>
          {selectLabel}
        </TextButton>
      </FareOptionActionSlot>}
    </FareOptionPane>
  )
}

function FareFamilyFareOption(props: Props) {
  const {
    selected,
    fareFamily,
    relativePrice,
    relativePriceUnit = 'adult',
    features = EmptyArray,
    onSelect = noop,
    onShowFareRules = noop,
    isReturnType = false,
    isPerAdultPrice,
    isBundledFlow = !isPerAdultPrice,
    overrideTotalCost,
    overrideAdultCost,
    luxPlusMemberCost,
    luxPlusMemberAdultCost,
    luxPlusRegularCost,
    luxPlusRegularAdultCost,
    isDomestic,
    isMultiCity,
    bundledHotelPrice = 0,
    cheapestReturningPrice = 0,
    journey,
    feederBudgetLabel,
    feederRegularBudgetLabel,
    rewardDepartingFareFamily,
    returningFareFamily,
    occupancies,
    showClassicRewardOption,
    showCashOption = true,
    isClassicRewardSelected,
  } = props

  const intl = useIntl()
  const { protectedFlightsName } = useInsuranceLabels()

  const viewFareRulesLabel = intl.formatMessage({
    id: 'Vilb7luUXek0FNx7NTCE',
    defaultMessage: 'View fare rules',
    description: 'Link text to view fare rules for a fare family option',
  })

  const economyCabinLabel = intl.formatMessage({
    id: 'sjBhVyeefjN8xsn2JKz6',
    defaultMessage: 'Economy',
    description: 'Flight cabin class label for economy in the flight search or booking flow',
  })

  const businessCabinLabel = intl.formatMessage({
    id: 'm1rEYIqGvFXJROBQkdJU',
    defaultMessage: 'Business',
    description: 'Flight cabin class label for business class in the flights flow',
  })

  const cabinDisplayOverrides = useMemo<Record<string, string>>(() => ({
    'economy': economyCabinLabel,
    'premium economy': economyCabinLabel,
    'economy extra': economyCabinLabel,
    'business': businessCabinLabel,
  }), [businessCabinLabel, economyCabinLabel])

  const loyaltyPrice: number = useMemo(() => {
    // Combined-return-fare modal (FareFamilySelectModalCombined): `overrideAdultCost` already
    // carries the per-adult round-trip total for a genuine paired return fare, so loyalty
    // points must be earned on that total, not this leg's own cost alone. `overrideTotalCost`
    // is the all-travellers total and would overstate points whenever the party has
    // children/infants.
    if (overrideAdultCost !== undefined && isPerAdultPrice) {
      return getPriceValue(overrideAdultCost)
    }

    if (isReturnType && isPerAdultPrice) {
      return overrideTotalCost || getPriceValue(fareFamily.costWithInsurance?.all || fareFamily.cost.all)
    }

    if (!isPerAdultPrice) {
      return getPriceValue(fareFamily.costWithInsurance?.all || fareFamily.cost.all)
    }

    return getPriceValue(fareFamily.costWithInsurance?.adult || fareFamily.cost.adult)
  }, [isReturnType, isPerAdultPrice, overrideTotalCost, overrideAdultCost, fareFamily])

  const cabinLabel = useMemo(() => {
    if (journey.provider !== CHARTER) {
      return fareFamily.cabin
    }

    const normalizedCabin = fareFamily.cabin.trim().toLowerCase()
    return cabinDisplayOverrides[normalizedCabin] ?? fareFamily.cabin
  }, [cabinDisplayOverrides, fareFamily.cabin, journey.provider])

  const cabinText = intl.formatMessage({
    id: 'G3DxtLdYqTo20JULWgoF',
    defaultMessage: 'Cabin: {cabinLabel}',
    description: 'Label showing the cabin class for a fare family option',
  }, { cabinLabel })

  const showLoyaltyPoints = useContext(FlightsLoyaltyContext)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const priceCaption = useSingleLegFlightTilePriceCaption({
    fareType: journey.fareType,
    isMultiCity,
    // Fare-upsell options always show the absolute total package cost, never a delta relative
    // to the currently selected fare, so the caption always resolves to its absolute-price copy.
    isRelativePrice: false,
    isStandalone: Boolean(isPerAdultPrice),
    isBundledFlow,
  })

  const flightSegmentsState = useAppSelector(getFlightSegmentsState)
  const loyaltyPointsEarnCalculations = useAppSelector(state => state.loyalty.pointsEarnCalculations)

  const previouslySelectedPointsCalculationRequests = useMemo((): Array<App.LoyaltyPointsEarnCalculationRequest | undefined> => {
    return flightSegmentsState.segmentsData.reduce((acc, data) => {
      if (data.loyaltyPointsKeys) {
        const pointsCalculationRequests = data.loyaltyPointsKeys.map((key: string) => loyaltyPointsEarnCalculations[key]?.data?.request)
        return [...acc, ...pointsCalculationRequests]
      }
      return acc
    }, [])
  }, [flightSegmentsState.segmentsData, loyaltyPointsEarnCalculations])

  /**
   * More info on why this is needed:
   * @see {@link loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKeyFromFlightItems.ts}
   */
  const firstPreviouslySelectedEarnKey = useMemo((): string | undefined => {
    // Skip propagation when a previously selected segment already has an earnKey
    const hasPreviousEarnKey = previouslySelectedPointsCalculationRequests.some((request) => request?.earnKey)
    if (hasPreviousEarnKey) return undefined

    const earnKeys = nonNullable(previouslySelectedPointsCalculationRequests.map((request) => {
      if (request && isLoyaltyCalculationRequestWithBonus(request) && request.earnKey) {
        return request.earnKey
      }
    }))

    return earnKeys[0]
  }, [previouslySelectedPointsCalculationRequests])

  const calculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    const type = getFlightLoyaltyProductType(fareFamily.cabin.toLowerCase(), isDomestic)
    const earnKey = firstPreviouslySelectedEarnKey || getLoyaltyEarnKey({
      loyaltyProductType: type,
      options: {
        originAirportCode: journey.flightGroup.flights[0].departureAirport,
        destinationAirportCode: last(journey.flightGroup.flights).arrivalAirport,
        carrier: journey.carrier,
        loyaltyEarnKey: fareFamily.loyaltyEarnKey,
      },
    })

    return [{
      loyaltyProductType: type,
      price: loyaltyPrice,
      earnKey,
      offerId: journey.id,
      packageId: '',
    }]
  }, [fareFamily, isDomestic, loyaltyPrice, journey, firstPreviouslySelectedEarnKey])

  const onViewFareRules = useCallback(() => {
    onShowFareRules(fareFamily.fareRulesUrl)
  }, [fareFamily, onShowFareRules])

  const title = fareFamily.withEmbeddedInsurance ? protectedFlightsName : fareFamily.fareFamily.description

  const isTravellerRelativePrice = relativePrice !== undefined && relativePriceUnit === 'traveller'
  const isAllTravellerRelativePrice = relativePrice !== undefined && !isPerAdultPrice

  const overrideMemberFareCost = isPerAdultPrice && !isTravellerRelativePrice ?
    luxPlusMemberAdultCost ?? overrideAdultCost :
    luxPlusMemberCost ?? overrideTotalCost

  // A genuine return-type fare (never Two-One-Ways or one-way, see `isReturnType`) carries a
  // round-trip total per adult alongside the per-leg `cost.adult`. Prefer it so the per-adult
  // basis shows the whole trip price, matching what `useSingleLegFlightTile` shows on the tile
  // this modal was opened from - falls back to the per-leg amount when absent (Two-One-Ways).
  const roundTripAdultCost = isReturnType ? fareFamily.cost.roundTripAdult : undefined
  const roundTripRegularAdultCost = isReturnType ? fareFamily.regularCost?.roundTripAdult : undefined

  const luxPlusMemberBaseFareCost = isAllTravellerRelativePrice || isTravellerRelativePrice
    ? fareFamily.cost.all
    : roundTripAdultCost ?? fareFamily.cost.adult
  const luxPlusMemberProtectedFareCost = isAllTravellerRelativePrice || isTravellerRelativePrice
    ? fareFamily.luxPlusCostWithInsurance?.all ?? fareFamily.costWithInsurance?.all ?? luxPlusMemberBaseFareCost
    : getProtectedRoundTripPrice(
      roundTripAdultCost,
      fareFamily.cost.adult,
      fareFamily.luxPlusCostWithInsurance?.adult ?? fareFamily.costWithInsurance?.adult ?? fareFamily.cost.adult,
    )!
  const luxPlusMemberFareCost = overrideMemberFareCost ?? (fareFamily.withEmbeddedInsurance
    ? luxPlusMemberProtectedFareCost
    : luxPlusMemberBaseFareCost)

  const overrideRegularFareCost = isPerAdultPrice && !isTravellerRelativePrice ? luxPlusRegularAdultCost : luxPlusRegularCost

  const luxPlusRegularBaseFareCost = isAllTravellerRelativePrice || isTravellerRelativePrice
    ? fareFamily.regularCost?.all : roundTripRegularAdultCost ?? fareFamily.regularCost?.adult
  const luxPlusProtectionCost = isAllTravellerRelativePrice || isTravellerRelativePrice
    ? (fareFamily.costWithInsurance?.all ?? fareFamily.cost.all) - fareFamily.cost.all
    : (fareFamily.costWithInsurance?.adult ?? fareFamily.cost.adult) - fareFamily.cost.adult
  const luxPlusRegularFareCost = overrideRegularFareCost ?? (luxPlusRegularBaseFareCost === undefined
    ? undefined
    : luxPlusRegularBaseFareCost + (fareFamily.withEmbeddedInsurance ? luxPlusProtectionCost : 0))

  const fareFamilyAllTravellerDisplayCost = overrideTotalCost ?? (fareFamily.costWithInsurance?.all || fareFamily.cost.all)
  // A Protect fare's `costWithInsurance.adult` is itself a per-leg figure, so round-trip-adjust it
  // the same way as the member/regular LuxPlus prices above rather than dropping the insurance
  // addition by preferring `roundTripAdultCost` outright.
  const fareFamilyAdultDisplayCost = overrideAdultCost ?? (fareFamily.costWithInsurance?.adult !== undefined
    ? getProtectedRoundTripPrice(roundTripAdultCost, fareFamily.cost.adult, fareFamily.costWithInsurance.adult)!
    : (roundTripAdultCost ?? fareFamily.cost.adult))
  const fareFamilyStandaloneDisplayCost = isTravellerRelativePrice ? fareFamilyAllTravellerDisplayCost : fareFamilyAdultDisplayCost

  // Fare-upsell options always show the absolute total package cost, never a delta relative
  // to the currently selected fare - so the fare's own total cost is used as-is.
  const luxPlusDisplayedAdultMemberFarePrice = luxPlusMemberFareCost
  const luxPlusDisplayedAdultNonMemberFarePrice = luxPlusRegularFareCost ?? 0
  // A Protect (withEmbeddedInsurance) fare can carry a real LuxPlus discount purely from the
  // embedded insurance's own 25% member discount, even when the underlying flight fare itself
  // isn't a LuxPlus fare - so isLuxPlusFare alone must not gate this off, or that insurance-only
  // discount silently disappears. The price-delta check below already ensures this only fires
  // when a genuine discount (fare and/or insurance) exists.
  //
  // For a feeder-bundle option, compare `feederBudgetLabel`/`feederRegularBudgetLabel` instead
  // of the raw per-leg costs above: on the returning leg of a progressive two-one-ways
  // selection, `luxPlusMemberFareCost`/`luxPlusRegularFareCost` are built by
  // `fareFamilyOverrideCost.ts` additively combining the already-committed departing leg's cost
  // with this option's own cost - but the departing leg's member `cost.all` was budget-snapped
  // at selection time while its `regularCost.all` never was, so that combination can invert the
  // comparison entirely. `feederBudgetLabel`/`feederRegularBudgetLabel` run the whole package
  // through the real budget rules directly and are never subject to that mismatch.
  const luxPlusMemberComparisonPrice = feederBudgetLabel ?? luxPlusDisplayedAdultMemberFarePrice
  const luxPlusNonMemberComparisonPrice = feederRegularBudgetLabel ?? luxPlusDisplayedAdultNonMemberFarePrice
  const showLuxPlusPrice = Boolean(
    (fareFamily.bookingInfo?.isLuxPlusFare || fareFamily.withEmbeddedInsurance) &&
    (fareFamily.regularCost || luxPlusRegularCost !== undefined) &&
    luxPlusMemberComparisonPrice < luxPlusNonMemberComparisonPrice,
  )
  const handleSelect = useCallback(() => {
    if (fareFamily.withEmbeddedInsurance) {
      trackClientEvent({
        subject: 'luxury_escapes_protect_fare',
        action: 'click',
        category: 'flight_search',
        type: 'interaction',
      })
    }

    onSelect({ fareFamily, pointsCalculationRequests: calculationRequests, isClassicReward: false })
  }, [onSelect, fareFamily, calculationRequests])

  const handleRewardSelect = useCallback((rewardPointsTotal: number) => {
    onSelect({ fareFamily, pointsCalculationRequests: calculationRequests, isClassicReward: true, rewardPointsTotal })
  }, [onSelect, fareFamily, calculationRequests])

  useEffect(() => {
    if (fareFamily.withEmbeddedInsurance) {
      trackClientEvent({
        subject: 'luxury_escapes_protect_fare',
        action: 'impression',
        category: 'flight_search',
        type: 'nonInteraction',
      })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const standardPrice = (() => {
    if (feederBudgetLabel !== undefined) {
      return getPriceValue(feederBudgetLabel)
    }

    if (!isPerAdultPrice) {
      return getPriceValue(bundledHotelPrice + fareFamilyAllTravellerDisplayCost + cheapestReturningPrice)
    }

    return getPriceValue(fareFamilyStandaloneDisplayCost)
  })()

  // For a feeder-bundle option, `feederBudgetLabel`/`feederRegularBudgetLabel` already carry
  // the correct hotel + charter + budget-snapped-leg total (the same rules `standardPrice`
  // uses above) - `bundledHotelPrice + fareCost + cheapestReturningPrice` below is the
  // plain non-feeder formula and would silently drop the charter leg and the FDA budget snap.
  const luxPlusMemberPrice = feederBudgetLabel !== undefined
    ? getPriceValue(feederBudgetLabel)
    : getPriceValue(isPerAdultPrice ? luxPlusDisplayedAdultMemberFarePrice : bundledHotelPrice + luxPlusMemberFareCost + cheapestReturningPrice)
  const luxPlusNonMemberPrice = feederRegularBudgetLabel !== undefined
    ? getPriceValue(feederRegularBudgetLabel)
    : getPriceValue(isPerAdultPrice ? luxPlusDisplayedAdultNonMemberFarePrice : bundledHotelPrice + (luxPlusRegularFareCost ?? luxPlusMemberFareCost) + cheapestReturningPrice)
  const showNonMemberLuxPlusPricing = showLuxPlusPrice && !canRedeemLuxPlusBenefits
  const price: FareFamilyUpsellOptionPrice = {
    value: showLuxPlusPrice ? luxPlusMemberPrice : standardPrice,
    previousValue: showLuxPlusPrice && canRedeemLuxPlusBenefits ? luxPlusNonMemberPrice : undefined,
    caption: priceCaption,
    secondaryCaption: showNonMemberLuxPlusPricing ? <FormattedMessage
      id="kDkl7bT4Gb-QmQ300neV"
      defaultMessage="Non-member {price}"
      description="Price caption label for non-member price display in the LuxPlus membership section"
      values={{
        price: <FormatCurrency
          value={luxPlusNonMemberPrice}
          format="roundedDollar"
        />,
      }}
    /> : undefined,
    showLuxPlusLogo: showNonMemberLuxPlusPricing,
    isLuxPlus: showLuxPlusPrice,
    saleUnit: relativePrice !== undefined && isTravellerRelativePrice ? 'traveller' : '',
  }
  const upsellFeatures = features.filter(feature => {
    return feature.type !== undefined && UPSELL_INCLUSION_FEATURE_TYPES.includes(feature.type)
  })
  const loyaltyPoints = showLoyaltyPoints ? <LoyaltyPointsEarn calculationRequests={calculationRequests} calculationType="exact" /> : undefined
  // This component only renders in an eligible AgentHub flow.
  const commission = <PriceRowAgentHubCommission
    offer={{ journey }}
    productType="flight"
    offerTotal={Math.abs(overrideTotalCost ?? (fareFamily.costWithInsurance?.all || fareFamily.cost.all))}
    offerId={journey.id}
  />

  const cashCard = (showCashOption || !showClassicRewardOption) ? <FareFamilyUpsellOptionCard
    cabinLabel={cabinText}
    commission={commission}
    fareRulesLabel={viewFareRulesLabel}
    features={upsellFeatures}
    loyaltyPoints={loyaltyPoints}
    onSelect={handleSelect}
    onViewFareRules={onViewFareRules}
    price={price}
    selected={selected && !isClassicRewardSelected}
    title={title}
  /> : undefined

  if (!showClassicRewardOption || !occupancies) {
    return cashCard ?? null
  }

  return (
    <VerticalSpacer gap={8}>
      {cashCard}
      {!cashCard && (
        <RewardOnlyHeading direction="vertical" gap={0}>
          <Heading variant="heading6">{title}</Heading>
          <Caption variant="smallalt">{cabinText}</Caption>
        </RewardOnlyHeading>
      )}
      <ClassicRewardFareOption
        fareFamily={fareFamily}
        rewardDepartingFareFamily={rewardDepartingFareFamily}
        returningFareFamily={returningFareFamily}
        isReturnType={isReturnType}
        occupancies={occupancies}
        selected={selected && !!isClassicRewardSelected}
        onSelect={handleRewardSelect}
      />
    </VerticalSpacer>
  )
}

export default FareFamilyFareOption
